<!-- https://www.figma.com/file/reAk2sU4YpRajpQu2YBOHk/007%2F1.-%D0%AD%D0%BB%D0%B5%D0%BC%D0%B5%D0%BD%D1%82%D1%8B?type=design&node-id=630-0&t=vSy6GU0zT13Ajcwf-0 -->
<template>
  <div
    ref="container"
    class="question-container"
    :style="containerStyle"
  >
    <template v-if="sendSuccess">
      <div class="question-container__success-image" />
      <div class="question-container__success-text">
        {{ attributes.success }}
      </div>
    </template>

    <template v-else>
      <div
        v-if="attributes.title"
        class="question-container__title"
      >
        {{ attributes.title }}
      </div>
      <p
        v-if="attributes.subtitle"
        class="question-container__subtitle"
      >
        {{ attributes.subtitle }}
      </p>
      <TextAreaCustom
        id="askQuestion"
        v-model="question"
        class="question"
        :placeholder="attributes.placeholder"
      >
        <button
          class="question__button"
          :disabled="!question"
          @click="sendQuestion(question)"
        >
          {{ attributes.button }}
        </button>
      </TextAreaCustom>
    </template>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import TextAreaCustom from '@/components/TextArea/TextArea.vue';
  import type { Question } from '~/types/components/AskQuestion';
  import { useIndexStore } from '~/store';

  export default defineNuxtComponent({
    name: 'AskQuestion',
    components: {
      TextAreaCustom,
    },

    props: {
      elem: {
        required: true,
        type: Object as PropType<TContent<Question>>,
      },
    },

    data() {
      return {
        question: '',
        sendSuccess: false,
        initialHeight: 0,
      };
    },

    setup() {
      return {
        indexStore: useIndexStore(),
      };
    },

    computed: {
      user() {
        return this.indexStore.user;
      },

      containerStyle(): boolean | Record<string, string> {
        // Минимальная высота для отображения изображения в полную высоту.
        const containerMinHeigth = 240;

        return (
          this.sendSuccess && {
            height: `${Math.max(this.initialHeight, containerMinHeigth)}px`,
          }
        );
      },

      attributes(): Question {
        return this.elem.attributes;
      },
    },

    async mounted() {
      if (this.$refs.container) {
        await this.$nextTick();
        this.initialHeight = (this.$refs.container as HTMLElement)?.clientHeight;
      }
    },

    methods: {
      async sendQuestion(question: string) {
        const { display_name = 'Без имени', integer_id = 0 } = this.user || {};

        try {
          await this.$specialApi.askQuestion(integer_id, display_name, question);
          this.sendSuccess = true;
        } catch (err) {
          console.error(err);
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '@devhacker/shared/styles/scrollbar.scss' as scrollbar;

  .question-container {
    padding: 24px 16px;
    margin: 24px 0px;
    background-color: #f3f5fc;
    border: 1px solid #e1eafd;
    border-radius: 4px;

    display: flex;
    flex-direction: column;
    justify-content: center;

    @include tablet {
      padding: 24px;
    }

    &__title {
      @include mediumText;
      font-family: $secondFont;
      font-weight: 600;

      @include tablet {
        @include bigText;
      }
    }

    &__subtitle {
      margin-top: 8px;
      @include normalText;

      @include tablet {
        @include mediumText;
      }
    }

    &__success {
      &-text {
        @include mediumText;

        text-align: center;
        font-family: $secondFont;
        font-weight: 600;
      }

      &-image {
        width: 160px;
        height: 160px;
        margin: 0 auto;
        background-image: url('../images/qna-success.svg');
      }
    }
  }

  .question {
    display: block;
    width: 100%;
    background-color: #fff;
    margin-top: 16px;
    min-height: 100px;
    border: 2px solid #e7e7e7;
    border-radius: 2px;
    padding: 12px 16px;

    :deep() {
      textarea {
        @include normalText;

        padding: 0;
        max-height: 328px;
        color: black;

        @include scrollbar.customScrollbar;

        @include tablet {
          max-height: 160px;
        }
      }
    }

    &__button {
      @include normalText;

      font-family: $mainFont;
      background-color: #fff;
      margin-top: 16px;
      border: none;
      font-weight: 700;
      color: var(--main-color-2);

      &:disabled {
        color: #777474;
      }

      &:not(:disabled) {
        @include hover {
          color: var(--hover-color-2);
          cursor: pointer;
        }
      }
    }
  }
</style>
